import React from "react";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SwitchSelector from "react-switch-selector";
import _ from "lodash";
// import useSound from "use-sound";
import { Pagination, Skeleton } from "antd";
import dayjs from "dayjs";
import { useMedia } from "react-use";

import { imgList } from "@constants/imgList";
// import { soundList } from "@constants/soundList";
import { device } from "@constants/styles";
import { InputIcon } from "@components/Inputs";
import { SelectSimple } from "@components/Selects";
import { ButtonBlock } from "@components/Buttons";
import { useMyTranslation } from "@components/Lang";
import {
  renderWifi,
  renderLight,
  renderComp,
  renderFan,
  renderDefrost,
  renderAlarm,
  renderDoor,
} from "@components/DeviceStatus";
import { displayImage } from "@utils/image";
import { toFixed, toFixedOrDash } from "@utils/string";
import { dateConvert } from "@utils/date";
import { apiGet } from "@apis/gateway";
import { loadingAction } from "@reduxs/Actions/storeAction";
import {
  fetchDeviceStatus,
  fetchLocationFilter,
  muteAlarm,
} from "@apis/monitoring";
import {
  filterAction,
  paginationAction,
  mapLocationDevicesAction,
  dataDevicesAction,
  isSoundPlayingAction,
  updateDateAction,
  isPlayAction,
} from "@reduxs/Actions/monitoringAction";
import {
  switchViewAction,
  switchSortAction,
} from "@reduxs/Actions/storageAction";
import { stateInterface } from "@reduxs/interface";

// Placeholder cards for the first load. Reuses the real <List> card so the
// page keeps its shape in both views — the list card is a row (image, text,
// action) while the grid card stacks — instead of showing bare white space
// under the global spinner.
function DeviceSkeleton({
  isList,
  count = 4,
}: {
  isList: boolean;
  count?: number;
}) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <List key={i} isList={isList} as="div">
          {isList ? (
            <>
              <Skeleton.Avatar
                active
                shape="square"
                size={72}
                style={{ borderRadius: 12, flex: "none" }}
              />
              <div style={{ flex: 1, padding: "0 20px", minWidth: 0 }}>
                <Skeleton
                  active
                  title={{ width: "28%" }}
                  paragraph={{ rows: 2, width: ["45%", "35%"] }}
                />
              </div>
              <Skeleton.Button
                active
                style={{ width: 110, height: 38, flex: "none" }}
              />
            </>
          ) : (
            <div style={{ width: "100%" }}>
              <Skeleton.Button
                active
                block
                style={{ height: 116, borderRadius: 12 }}
              />
              <div style={{ margin: "14px 0 0" }}>
                <Skeleton
                  active
                  title={{ width: "60%" }}
                  paragraph={{ rows: 2, width: ["80%", "50%"] }}
                />
              </div>
            </div>
          )}
        </List>
      ))}
    </>
  );
}

export default function Device() {
  const { t } = useMyTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const language = useSelector(
    (state: stateInterface) => state.storage.language
  );
  const filter = useSelector(
    (state: stateInterface) => state.monitoring.filter
  );
  const listStatus = useSelector(
    (state: stateInterface) => state.monitoring.listStatus
  );
  const listLocation = useSelector(
    (state: stateInterface) => state.monitoring.listLocation
  );
  const view = useSelector(
    (state: stateInterface) => state?.storage?.view ?? "list"
  );
  const sort = useSelector(
    (state: stateInterface) => state?.storage?.sort ?? "char_asc"
  );
  const pagination = useSelector(
    (state: stateInterface) => state.monitoring.pagination
  );
  const dataRaw = useSelector(
    (state: stateInterface) => state.monitoring.dataDevices
  );
  const sound = useSelector(
    (state: stateInterface) => state?.storage?.sound ?? "off"
  );
  const isSoundPlaying = useSelector(
    (state: stateInterface) => state.monitoring.isSoundPlaying
  );
  const updateDate = useSelector(
    (state: stateInterface) => state.monitoring.updateDate
  );
  const { name, status, location, area } = filter;
  // "no devices found" is only true once the first fetch has actually returned —
  // before that the list is empty simply because nothing has arrived yet
  const [loaded, setLoaded] = useState(false);
  const [loadSound, setLoadSound] = useState(false);
  // const [play, { stop }] = useSound(soundList.beeps, {
  //   loop: true,
  //   interrupt: true,
  //   onload: () => setLoadSound(true),
  // });
  const [stuck, setStuck] = useState(false);
  const ref: any = React.createRef();
  const styles = stuck ? {} : {};
  const isTablet = useMedia("(max-width: 1024px)");
  const isMobile = useMedia("(max-width: 500px)");

  const position = "top";

  const inlineStyles: any = {
    position: "sticky",
    zIndex: 999,
    [position]: isMobile ? -414 : isTablet ? -218 : -70,
    ...styles,
  };

  const options = [
    {
      label: (
        <div className="sw__choose">
          <img src={imgList.ico_list} alt="" />
        </div>
      ),
      value: "list",
    },
    {
      label: (
        <div className="sw__choose">
          <img src={imgList.ico_grid} alt="" />
        </div>
      ),
      value: "grid",
    },
  ];
  const onChangeSwitch = (newValue: any) => {
    dispatch(switchViewAction(newValue));
  };
  // Derived view state — computed in ONE render pass with useMemo instead of the
  // old effect chain (dataRaw → setDataFilter → setPagination → dispatch count),
  // which fired ~6 re-renders per socket "devices" event (worse the more
  // devices, since each event re-ran _.orderBy over the whole list).
  const dataFilter = useMemo(() => {
    let arr = dataRaw ?? [];
    if (name !== "") {
      arr = arr.filter((e: any) =>
        (e?.deviceInfo?.name ?? "").toLowerCase().includes(name.toLowerCase())
      );
    }
    if (status.length > 0) {
      arr = arr.filter(
        (e: any) => status.indexOf(e?.deviceStatus?.status ?? "") > -1
      );
    }
    if (location.length > 0) {
      arr = arr.filter((e: any) => location.indexOf(e?.locationID ?? "") > -1);
    }
    if (sort === "char_asc") arr = _.orderBy(arr, "deviceInfo.name", "asc");
    else if (sort === "char_desc") arr = _.orderBy(arr, "deviceInfo.name", "desc");
    else if (sort === "update_desc") arr = _.orderBy(arr, "createdAt", "desc");
    else if (sort === "update_asc") arr = _.orderBy(arr, "createdAt", "asc");
    return arr;
  }, [dataRaw, name, status, location, sort]);

  const dataPagination = useMemo(() => {
    const start =
      pagination.page === 1 ? 0 : (pagination.page - 1) * pagination.pageSize;
    const end = pagination.page * pagination.pageSize - 1;
    return (dataFilter ?? []).filter(
      (e: any, index: number) => index >= start && index <= end
    );
  }, [dataFilter, pagination]);

  // Status tallies for the header badges — derived locally (was dispatched to
  // redux via an effect, but only this page reads it, so a memo avoids the
  // round-trip and one extra render). /monitoring keeps its own count dispatch.
  const count = useMemo(() => {
    let alarm = 0;
    let normal = 0;
    let offline = 0;
    let powerOff = 0;
    (dataFilter ?? []).forEach((e: any) => {
      const s = e?.deviceStatus?.filterStatus;
      if (s === "normal") normal++;
      else if (s === "offline") offline++;
      else if (s === "powerOff") powerOff++;
      else if (s === "alarm") alarm++;
    });
    return {
      alarm,
      normal,
      offline,
      powerOff,
      totalDevices: (dataFilter ?? []).length,
    };
  }, [dataFilter]);
  useEffect(() => {
    const cachedRef: any = ref.current;
    const observer = new IntersectionObserver(
      ([e]) => {
        setStuck(e.intersectionRatio < 1);
      },
      { threshold: [1] }
    );
    observer.observe(cachedRef);
    return () => observer.unobserve(cachedRef);
  }, [ref]);
  useEffect(() => {
    return () => {
      dispatch(dataDevicesAction([]));
    };
  }, []);
  // checkSound removed — the alarm sound is now driven globally by the
  // Layout/Default alarm registry. The old page-level check also had a bug:
  // it scanned the FILTERED list, so filtering the alarming device out of
  // view silenced the sound while the alarm was still active.
  useEffect(() => {
    initData();
  }, []);
  useEffect(() => {
    dispatch(updateDateAction(dayjs()));
  }, [dataRaw]);
  const initData = async () => {
    dispatch(loadingAction(true));
    // The three calls are independent (each writes its own redux slice:
    // dataDevices / listStatus / listLocation), so awaiting them in sequence
    // just stacked their latencies behind the loading backdrop — on production
    // that's ~1.4s + ~1.4s + ~1.9s ≈ 4.7s of TTFB. Fire them together instead:
    // the page waits for the slowest one (~1.9s), not their sum.
    await Promise.all([
      getMonitoring(),
      fetchDeviceStatus(dispatch, language),
      fetchLocationFilter(dispatch, language),
    ]);
    setLoaded(true);
    dispatch(loadingAction(false));
  };
  const getMonitoring = async () => {
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/monitoring`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      let array: any[] = [];
      let arrayMac: any[] = [];
      (res?.data?.data?.data ?? []).forEach((e: any, index: number) => {
        (e?.devices ?? []).forEach((e2: any, index2: number) => {
          let obj = {
            ...e2,
            location: e?.name ?? "",
            locationID: e?.id ?? "",
          };
          let objMac = {
            macAddress: e2?.deviceInfo?.macAddress ?? "",
          };
          array.push(obj);
          arrayMac.push(objMac);
        });
      });
      dispatch(mapLocationDevicesAction(arrayMac));
      dispatch(dataDevicesAction(array));
    }
  };
  const renderStatus = (status = "", statusText = "") => {
    let color = "";
    switch (status) {
      case "normal":
        color = "color-status-normal";
        break;
      case "powerOff":
        color = "color-status-poweroff";
        break;
      case "offline":
        color = "color-status-offline";
        break;
      case "alarm":
        color = "color-status-alarm";
        break;
      case "updating":
        color = "color-status-updating";
        break;
      default:
        color = "color-status-normal";
        break;
    }
    return (
      <div className={`weight-600 size-20 font-sukhumvitSet-text ${color}`}>
        {t(`monitoring.${status}`)}
      </div>
    );
  };
  const renderSound = (macAddress = "", soundAlarm = false, alarm = false) => {
    if (alarm) {
      if (soundAlarm) {
        return (
          <img
            className="pointer"
            src={imgList.ico_sound}
            alt=""
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              muteAlarm(dispatch, language, macAddress);
            }}
          />
        );
      } else {
        return <img src={imgList.ico_sound_disable} alt="" />;
      }
    } else {
      return <></>;
    }
  };
  const listSort = [
    {
      label: t("devices.order1"),
      value: "char_asc",
    },
    {
      label: t("devices.order2"),
      value: "char_desc",
    },
    {
      label: t("devices.order3"),
      value: "update_desc",
    },
    {
      label: t("devices.order4"),
      value: "update_asc",
    },
  ];
  return (
    <Container className="detail" isList={view === "list"}>
      <div className="detail__head" ref={ref} style={inlineStyles}>
        <div className="detail__top">
          <div>
            <div className="font-inter size-30 color-blue828 weight-600">
              {t("devices.head")}
            </div>
          </div>
          {!stuck && !isMobile && !isTablet && (
            <div className="detail__text font-sukhumvitSet-text size-16 color-gray474">
              <img src={imgList.ico_clock} alt="" />
              {t("monitoring.lastupdate")}:{" "}
              <span className="color-black">{dateConvert(updateDate)}</span>
            </div>
          )}
          <div className="detail__text detail__text--tablet font-sukhumvitSet-text size-16 color-gray474">
            <img src={imgList.ico_clock} alt="" />
            {t("monitoring.lastupdate")}:{" "}
            <span className="color-black">{dateConvert(updateDate)}</span>
          </div>
        </div>
        <Filter className="filter">
          <div className="filter__box filter__box--1">
            <InputIcon
              type="text"
              value={name}
              placeholder={t("monitoring.search")}
              onChange={(e) => {
                dispatch(
                  filterAction({
                    ...filter,
                    name: e.target.value,
                  })
                );
                dispatch(
                  paginationAction({
                    ...pagination,
                    page: 1,
                  })
                );
              }}
            />
          </div>
          <div className="filter__box filter__box--2">
            <SelectSimple
              mode="multiple"
              id="status"
              labelText={t("monitoring.status")}
              value={status}
              options={listStatus.map((e) => {
                return {
                  label: t(`monitoring.${e?.filterStatus ?? ""}`),
                  value: e?.filterStatus ?? "",
                };
              })}
              placeholder={t("monitoring.all")}
              maxTagCount={0}
              maxTagPlaceholder={
                <div>
                  {status.length > 1
                    ? `${t("monitoring.show")} (${status.length})`
                    : `${t(`monitoring.${status?.[0] ?? ""}`)}`}
                </div>
              }
              classNameWarp=""
              onChange={(e) => {
                dispatch(
                  filterAction({
                    ...filter,
                    status: e,
                  })
                );
                dispatch(
                  paginationAction({
                    ...pagination,
                    page: 1,
                  })
                );
              }}
            />
          </div>
          <div className="filter__box filter__box--2">
            <SelectSimple
              optionFilterProp="label"
              mode="multiple"
              id="location"
              labelText={t("monitoring.location")}
              value={location}
              options={listLocation.map((e) => {
                return {
                  label: e?.name ?? "",
                  value: e?.id ?? "",
                };
              })}
              placeholder={t("monitoring.all")}
              maxTagCount={0}
              maxTagPlaceholder={
                <div>
                  {location.length > 1
                    ? `${t("monitoring.show")} (${location.length})`
                    : `${
                        listLocation.filter(
                          (e) => e.id === location?.[0] ?? ""
                        )?.[0]?.name ?? ""
                      }`}
                </div>
              }
              classNameWarp=""
              onChange={(e) => {
                dispatch(
                  filterAction({
                    ...filter,
                    location: e,
                  })
                );
                dispatch(
                  paginationAction({
                    ...pagination,
                    page: 1,
                  })
                );
              }}
            />
          </div>
          {/* <div className="filter__box filter__box--2">
            <SelectSimple
              mode="multiple"
              id="area"
              labelText={t("monitoring.area")}
              value={area}
              options={[
                {
                  label: t("monitoring.all"),
                  value: "all",
                },
              ]}
              placeholder={t("monitoring.all")}
              classNameWarp=""
              disabled
            />
          </div> */}
        </Filter>
        <Headline className="headline">
          <div className="headline__left">
            <div className="headline__head size-20 weight-700 font-sukhumvitSet-bold">
              {t("devices.device")} ({dataFilter.length})
            </div>
            <TotalDevices className="total">
              <div className="total__box total__box--1">
                <img src={imgList.icoCatNormal} alt="" className="total__img" />
                <div className="font-sukhumvitSet-bold size-16 total__text-number">
                  {count?.normal ?? 0}
                </div>
              </div>
              <div className="total__box total__box--2">
                <img src={imgList.icoCatPower} alt="" className="total__img" />
                <div className="font-sukhumvitSet-bold size-16 total__text-number">
                  {count?.powerOff ?? 0}
                </div>
              </div>
              <div className="total__box total__box--3">
                <img
                  src={imgList.icoCatOffline}
                  alt=""
                  className="total__img"
                />
                <div className="font-sukhumvitSet-bold size-16 total__text-number">
                  {count?.offline ?? 0}
                </div>
              </div>
              <div className="total__box total__box--4 last">
                <img src={imgList.icoCatAlarm} alt="" className="total__img" />
                <div className="font-sukhumvitSet-bold size-16 total__text-number">
                  {count?.alarm ?? 0}
                </div>
              </div>
            </TotalDevices>
            {stuck && (
              <div
                className={`detail__text ${
                  stuck ? "detail__text--show" : "detail__text--hide"
                } font-sukhumvitSet-text size-16 color-gray474`}
              >
                <img src={imgList.ico_clock} alt="" />
                {t("monitoring.lastupdate")}:{" "}
                <span className="color-black">{dateConvert(updateDate)}</span>
              </div>
            )}
          </div>
          <OrderBy>
            <SwitchWarp className="sw">
              <SwitchSelector
                forcedSelectedIndex={options.findIndex(
                  ({ value }) => value === view
                )}
                onChange={onChangeSwitch}
                options={options}
                backgroundColor={"#353b48"}
                fontColor={"#f5f6fa"}
              />
            </SwitchWarp>
            <SelectSimple
              id="sort"
              labelText=""
              value={sort}
              options={listSort}
              classNameWarp="select-sort"
              placement="bottomRight"
              onChange={(e) => {
                dispatch(switchSortAction(e));
              }}
            />
          </OrderBy>
        </Headline>
      </div>
      <div
        className={`detail__warp ${
          (dataPagination ?? []).length <= 0 && dataFilter.length > 0
            ? "detail__warp--100"
            : ""
        }`}
      >
        <div className="detail__content">
          {!loaded ? (
            <DeviceSkeleton isList={view === "list"} />
          ) : (dataPagination ?? []).length <= 0 && dataFilter.length > 0 ? (
            <div className="detail__btn">
              <ButtonBlock
                text={t("devices.showdevice")}
                className="modal__btn--cf"
                onClick={() =>
                  dispatch(
                    paginationAction({
                      ...pagination,
                      page: 1,
                    })
                  )
                }
              />
            </div>
          ) : (dataPagination ?? []).length <= 0 && loaded ? (
            // only after the first fetch has returned — an empty list during
            // loading is not "you have no devices"
            <NoDevice>
              <img src={imgList.icoFaq} alt="" />
              <div className="color-black size-16 font-sukhumvitSet-bold">
                {t("devices.cannotfind")}
              </div>
            </NoDevice>
          ) : (
            <></>
          )}
          {(dataPagination ?? []).map((e, index) => (
            <List
              className="list"
              key={index}
              isList={view === "list"}
              // onClick={
              //   view === "list"
              //     ? () => {}
              //     : () =>
              //         navigate(
              //           `/device-detail/${e?.locationID ?? ""}/${
              //             e?.deviceInfo?.macAddress ?? ""
              //           }`
              //         )
              // }
              onClick={
                view === "list"
                  ? () => {}
                  : () =>
                      navigate(
                        `/device-detail/${e?.deviceInfo?.macAddress ?? ""}`
                      )
              }
            >
              <div className="list__cover">
                <img
                  src={
                    e?.deviceInfo?.image ?? ""
                      ? displayImage(e?.deviceInfo?.image ?? "")
                      : imgList.ex
                  }
                  alt=""
                />
              </div>
              <div className="list__left">
                <div className="list__name">
                  <div className="size-16 weight-600 font-sukhumvitSet-text color-black">
                    {e?.deviceInfo?.name ?? ""}
                  </div>
                  {renderSound(
                    e?.controlInfo?.macAddress ?? "",
                    e?.controlInfo?.soundAlarm ?? false,
                    e?.controlInfo?.control?.alarm ?? false
                  )}
                </div>
                <div className="list__text-group">
                  <div className="list__text list__text--sp">
                    <img src={imgList.ico_location_gray} alt="" />
                    <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                      {e?.location ?? ""}
                    </div>
                  </div>
                </div>
                <div className="list__text-group">
                  <div className="list__text list__text--sp">
                    <img src={imgList.ico_user_gray} alt="" />
                    <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                      {e?.isOwner ?? false
                        ? t("monitoring.mydevice")
                        : t("monitoring.sharedevice")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="list__right">
                <div>
                  <div className="list__status">
                    <div className="list__status-group">
                      <div className="list__status-render">
                        <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                          {t(
                            (e?.deviceStatus?.status ?? "") === "offline"
                              ? "monitoring.lasttemp"
                              : "monitoring.currenttemp"
                          )}
                        </div>
                        <div className="color-grayf4f weight-600 size-20 font-sukhumvitSet-text">
                          {toFixedOrDash(e?.deviceInfo?.currentTemp, 1)}
                          {toFixedOrDash(e?.deviceInfo?.currentTemp, 1) !==
                            "-" && <span className="size-12">°C</span>}
                        </div>
                      </div>
                      <div className="list__status-render">
                        <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                          {t("monitoring.status")}
                        </div>
                        {renderStatus(
                          e?.deviceStatus?.status ?? "",
                          e?.deviceStatus?.statusDisplayText ?? ""
                        )}
                      </div>
                      <div className="list__status-render">
                        <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                          {t("monitoring.mode")}
                        </div>
                        <div className="color-grayf4f weight-600 size-20 font-sukhumvitSet-text">
                          {/* {e?.deviceStatus?.modeDisplayText ?? ""} */}
                          {t(`monitoring.${e?.deviceStatus?.mode ?? ""}`)}
                        </div>
                      </div>
                    </div>
                    <div className="list__group">
                      {renderWifi(
                        e?.controlInfo?.control?.wifi ?? false,
                        e?.controlInfo?.rssi ?? null,
                        e?.controlInfo?.wifiStrength ?? "good"
                      )}
                      {renderLight(e?.controlInfo?.control?.light ?? false)}
                      {renderDefrost(
                        e?.deviceStatus?.status ?? "",
                        e?.controlInfo?.control?.defrost ?? false,
                        e?.controlInfo?.flashing?.defrost ?? false
                      )}
                      {renderFan(
                        e?.deviceStatus?.status ?? "",
                        e?.controlInfo?.control?.fan ?? false,
                        e?.controlInfo?.flashing?.fan ?? false
                      )}
                      {renderComp(
                        e?.deviceStatus?.status ?? "",
                        e?.controlInfo?.control?.comp ?? false,
                        e?.controlInfo?.flashing?.comp ?? false
                      )}
                      {renderAlarm(
                        e?.controlInfo?.control?.alarm ?? false,
                        e?.controlInfo?.flashing?.alarm ?? false
                      )}
                      {renderDoor(
                        e?.deviceStatus?.status ?? "",
                        e?.controlInfo?.control?.door ?? false,
                        e?.controlInfo?.flashing?.door ?? false
                      )}
                    </div>
                  </div>
                  <div
                    className="list__btn color-black size-14 weight-600 font-sukhumvitSet-text"
                    // onClick={() =>
                    //   navigate(
                    //     `/device-detail/${e?.locationID ?? ""}/${
                    //       e?.deviceInfo?.macAddress ?? ""
                    //     }`
                    //   )
                    // }
                    onClick={() =>
                      navigate(
                        `/device-detail/${e?.deviceInfo?.macAddress ?? ""}`
                      )
                    }
                  >
                    {t("monitoring.detail")}
                  </div>
                </div>
              </div>
            </List>
          ))}
        </div>
      </div>
      {(dataPagination ?? []).length > 0 && (
        <PaginationWarp>
          <Pagination
            current={pagination.page}
            total={dataFilter.length}
            pageSize={pagination.pageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            showSizeChanger={true}
            showLessItems={true}
            // same shape the metric pages and the activity log use: "รายการ /
            // หน้า" instead of a bare "หน้า" (which read as "10 pages"), plus a
            // range summary so the count means something
            locale={{ items_per_page: t("detail.perpage") }}
            showTotal={(total: number, range: [number, number]) =>
              `${t("detail.showing")} ${range[0].toLocaleString(
                "en-US"
              )}-${range[1].toLocaleString("en-US")} ${t(
                "detail.oftotal"
              )} ${total.toLocaleString("en-US")} ${t("devices.devices")}`
            }
            onChange={(page, pageSize) => {
              dispatch(
                paginationAction({
                  ...pagination,
                  page: page,
                  pageSize: pageSize,
                })
              );
            }}
          />
        </PaginationWarp>
      )}
    </Container>
  );
}

interface IList {
  isList: boolean;
}
const Stuck = styled.div`
  width: 100%;
`;
const TotalDevices = styled.div`
  // position: fixed;
  max-width: 23.889vw;
  background: #ffffff;
  border-radius: 0.417vw;
  display: flex;
  align-items: center;
  padding: 0.347vw 0;
  margin: 0 1.528vw;
  border: 0.035vw solid #dce0e4;
  > img {
    width: 1.389vw;
    margin: 0 0.556vw 0 0;
  }
  @media ${device.desktopL} {
    max-width: 344px;
    border-radius: 6px;
    padding: 5px 0;
    margin: 0 22px;
    border: 0.5px solid #dce0e4;
    > img {
      width: 20px;
      margin: 0 8px 0 0;
    }
  }
  @media (max-width: 395px) {
    margin: 0;
  }
  .total {
    &__box {
      display: flex;
      align-items: center;
      border-right: 0.035vw solid rgba(0, 0, 0, 0.2);
      padding: 0 0.833vw;
      height: 1.944vw;
      &.last {
        border-right: none;
      }
      &--1 {
        color: #12b76a;
      }
      &--2 {
        color: #656565;
      }
      &--3 {
        color: #959595;
      }
      &--4 {
        color: #d93a36;
      }
      @media ${device.desktopL} {
        border-right: 0.5px solid rgba(0, 0, 0, 0.2);
        padding: 0 12px;
        height: 28px;
      }
    }
    &__text {
      margin: 0 0 -0.486vw;
      @media ${device.desktopL} {
        margin: 0 0 -7px;
      }
    }
    &__number {
      margin: 0 0 0 0.347vw;
      @media ${device.desktopL} {
        margin: 0 0 0 5px;
      }
    }
    &__img {
      width: 0.972vw;
      margin: 0 0.417vw 0 0;
      @media ${device.desktopL} {
        width: 14px;
        margin: 0 6px 0 0;
      }
    }
    &__text-number {
      margin: 0.139vw 0 0;
      @media ${device.desktopL} {
        margin: 2px 0 0;
      }
    }
    &__box-total {
      margin: 0 0 -0.278vw;
      @media ${device.desktopL} {
        margin: 0 0 -4px;
      }
    }
  }
`;
const NoDevice = styled.div`
  width: 100%;
  height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 20px;
    display: block;
    margin: -4px 0 0;
  }
`;
const PaginationWarp = styled.div`
  width: 100%;
  text-align: right;
  padding: 24px 40px;
  margin: 0 0 -32px;
  background: #fff;
  border-radius: 0 0px 0 40px;
  /* Same arrangement the tables use (components/Table): page-size selector on
     the far LEFT, range summary CENTRED, page navigation on the RIGHT. This is
     a standalone <Pagination>, so it targets .ant-pagination rather than
     .ant-table-pagination — the rules are otherwise identical. */
  & .ant-pagination {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  & .ant-pagination-options {
    order: -2;
    margin-left: 0;
    margin-right: 0;
  }
  & .ant-pagination-total-text {
    order: -1;
    margin: 0 auto;
    color: #64748b;
  }
  & .ant-pagination-item-active a {
    color: rgba(0, 0, 0, 0.85);
  }
  & .ant-pagination-item-active {
    background: #febc11;
    border-color: #febc11;
  }
  & .ant-pagination-item:hover {
    background: #febc11;
    border-color: #febc11;
  }
  & .ant-pagination-item:hover a {
    color: rgba(0, 0, 0, 0.85);
  }
  & .ant-pagination-next:focus-visible .ant-pagination-item-link,
  .ant-pagination-next:hover .ant-pagination-item-link,
  .ant-pagination-prev:focus-visible .ant-pagination-item-link,
  .ant-pagination-prev:hover .ant-pagination-item-link {
    color: rgba(0, 0, 0, 0.85);
    border-color: #febc11;
    background: #febc11;
  }
  & .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: #febc11;
  }
  & .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: #febc11;
  }
  /* On a phone the three pieces don't fit on one line, and letting them wrap
     where they land strands the "next" arrow alone on a second row. Give the
     summary its own line and keep the controls together underneath. */
  @media ${device.mobileLL} {
    padding: 20px 18px;
    & .ant-pagination {
      justify-content: center;
    }
    & .ant-pagination-total-text {
      order: -3;
      width: 100%;
      margin: 0 0 12px;
      text-align: center;
    }
    & .ant-pagination-options {
      order: 2;
      margin-left: 12px;
    }
  }
`;
const OrderBy = styled.div`
  width: calc(180px + 16px + 84px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  & .select-sort {
    width: 180px;
    margin: 0 0 0 16px;
  }
  & .ant-select-selector {
    height: 40px !important;
  }
`;
const SwitchWarp = styled.div`
  width: 84px;
  & .react-switch-selector-wrapper {
    background: #f9fafb;
    border-radius: 0.556vw;
  }
  & .react-switch-selector-wrapper::before {
    background: #ffffff;
    border: none;
    box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.1),
      0px 1px 2px rgba(16, 24, 40, 0.06);
    border-radius: 0.556vw;
    height: calc(100% - 0.417vw);
    width: calc(50% - 0.417vw);
    margin-left: 0.208vw;
  }
  @media ${device.desktopL} {
    & .react-switch-selector-wrapper {
      border-radius: 8px;
    }
    & .react-switch-selector-wrapper::before {
      height: calc(100% - 6px);
      width: calc(50% - 6px);
      border-radius: 8px;
      margin-left: 3px;
    }
  }
  .sw {
    &__choose {
      width: 100%;
      height: 2.778vw;
      display: flex;
      align-items: center;
      justify-content: center;
      > img {
        width: 1.111vw;
      }
      @media ${device.desktopL} {
        height: 40px;
        > img {
          width: 16px;
        }
      }
    }
  }
`;
const List = styled.div<IList>`
  position: relative;
  width: ${(props) => (props.isList ? "100%" : "calc(50% - 16px)")};
  border-radius: 1.111vw;
  border: 0.069vw solid #eaeaea;
  background: #fff;
  box-shadow: 0px 0.278vw 1.389vw 0px rgba(227, 227, 227, 0.25);
  padding: 1.389vw;
  margin: 1.111vw ${(props) => (props.isList ? "0" : "0.556vw")} 0;
  display: flex;
  flex-wrap: ${(props) => (props.isList ? "unset" : "wrap")};
  cursor: ${(props) => (props.isList ? "default" : "pointer")};
  &:hover {
    border: 0.069vw solid #febc11;
  }
  & .pointer {
    cursor: pointer;
  }
  @media ${device.desktopL} {
    width: ${(props) => (props.isList ? "100%" : "calc(25% - 16px)")};
    border-radius: 16px;
    border: 1px solid #eaeaea;
    box-shadow: 0px 4px 20px 0px rgba(227, 227, 227, 0.25);
    padding: 20px;
    margin: 16px ${(props) => (props.isList ? "0" : "8px")} 0;
    &:hover {
      border: 1px solid #febc11;
    }
  }
  @media ${device.desktopLL} {
    width: ${(props) => (props.isList ? "100%" : "calc(50% - 16px)")};
    padding: ${(props) => (props.isList ? "20px" : "16px")};
  }
  @media ${device.laptop} {
    width: ${(props) => (props.isList ? "100%" : "calc(50% - 16px)")};
    // margin: 16px 0 0px;
    flex-wrap: wrap;
  }
  @media ${device.mobileLL} {
    width: 100%;
    flex-wrap: wrap;
  }
  .list {
    &__cover {
      position: relative;
      width: 5.556vw;
      height: 5.556vw;
      border-radius: 0.556vw;
      margin: 0 1.667vw 0 0;
      overflow: hidden;
      > img {
        position: absolute;
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
        width: 100%;
        height: 100%;
        max-width: unset;
        max-height: unset;
        object-fit: cover;
      }
      @media ${device.desktopL} {
        width: 80px;
        height: 80px;
        border-radius: 8px;
        margin: 0 24px 0 0;
      }
      @media ${device.laptop} {
        margin: 0 14px 0 0;
      }
    }
    &__left {
      width: calc(
        ${(props) => (props.isList ? "45%" : "100%")} - 5.556vw - 1.667vw
      );
      padding: 0 ${(props) => (props.isList ? "1.667vw" : "0")} 0 0;
      border-right: ${(props) =>
        props.isList ? "0.069vw solid #e4e4e4" : "none"};
      @media ${device.desktopL} {
        width: calc(
          ${(props) => (props.isList ? "45%" : "100%")} - 80px - 24px
        );
        padding: 0 ${(props) => (props.isList ? "24px" : "0")} 0 0;
        border-right: ${(props) =>
          props.isList ? "1px solid #e4e4e4" : "none"};
      }
      @media ${device.laptop} {
        width: calc(
          ${(props) => (props.isList ? "100%" : "100%")} - 80px - 14px
        );
        border-right: none;
        padding: 0;
      }
      @media ${device.mobileLL} {
        width: ${(props) =>
          props.isList
            ? "calc(100% - 80px - 14px)"
            : "calc(100% - 80px - 14px)"};
        margin: ${(props) => (props.isList ? "0" : "0")} 0 0;
      }
    }
    &__name {
      width: 100%;
      display: flex;
      align-items: flex-start;
      > div {
        max-width: calc(100% - 1.736vw - 0.972vw);
        margin: 0 0.972vw 0 0;
      }
      > img {
        width: 1.736vw;
      }
      @media ${device.desktopL} {
        > div {
          max-width: calc(100% - 25px - 14px);
          margin: 0 14px 0 0;
        }
        > img {
          width: 25px;
        }
      }
    }
    &__text-group {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin: 0.833vw 0 0;
      @media ${device.desktopL} {
        margin: 12px 0 0;
      }
    }
    &__text {
      width: 48%;
      display: flex;
      align-items: flex-start;
      > img {
        width: 1.111vw;
        margin: 0 0.417vw 0 0;
      }
      > div {
        width: calc(100% - 1.528vw);
      }
      &--sp {
        width: 100%;
      }
      @media ${device.desktopL} {
        > img {
          width: 16px;
          margin: 0 6px 0 0;
        }
        > div {
          width: calc(100% - 22px);
        }
      }
    }
    &__right {
      width: ${(props) => (props.isList ? "55%" : "100%")};
      padding: ${(props) => (props.isList ? "0" : "0.833vw")} 0 0
        ${(props) => (props.isList ? "1.667vw" : "7.361vw")};
      > div {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      @media ${device.desktopL} {
        padding: ${(props) => (props.isList ? "0" : "12px")} 0 0
          ${(props) => (props.isList ? "24px" : "106px")};
      }
      @media ${device.laptop} {
        width: ${(props) => (props.isList ? "100%" : "100%")};
        padding: ${(props) => (props.isList ? "0" : "12px")} 0 0
          ${(props) => (props.isList ? "0" : "96px")};
        margin: ${(props) => (props.isList ? "12px" : "0")} 0 0;
      }
      @media ${device.tablet} {
        padding: ${(props) => (props.isList ? "0" : "12px")} 0 0
          ${(props) => (props.isList ? "0" : "0")};
      }
      @media ${device.mobileLL} {
        width: 100%;
        padding: 0;
        margin: 16px 0 0;
        > div {
          flex-wrap: wrap;
        }
      }
    }
    &__status {
      width: ${(props) => (props.isList ? "calc(100% - 7.639vw)" : "100%")};
      @media ${device.desktopL} {
        width: ${(props) => (props.isList ? "calc(100% - 110px)" : "100%")};
        padding: ${(props) => (props.isList ? "0 20px 0 0" : "0")};
      }
      @media ${device.mobileLL} {
        width: 100%;
      }
    }
    &__status-group {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }
    &__status-render {
      width: calc(95% / 3);
      & .size-20 {
        line-height: 1.2;
        word-break: break-all;
      }
      @media ${device.laptopSL} {
        & .size-20 {
          font-size: 17px;
        }
      }
      @media ${device.laptop} {
        & .size-20 {
          font-size: 16px;
        }
      }
    }
    &__btn {
      display: ${(props) => (props.isList ? "flex" : "none")};
      align-items: center;
      justify-content: center;
      width: 7.639vw;
      height: 2.778vw;
      border-radius: 0.556vw;
      border: 0.069vw solid #b7b7b7;
      cursor: pointer;
      @media ${device.desktopL} {
        width: 110px;
        height: 40px;
        border-radius: 8px;
        border: 1px solid #b7b7b7;
      }
      @media ${device.laptop} {
        position: ${(props) => (props.isList ? "absolute" : "relative")};
        right: ${(props) => (props.isList ? "16px" : "unset")};
        top: ${(props) => (props.isList ? "50%" : "unset")};
        transform: ${(props) =>
          props.isList ? "translate(0, -50%)" : "unset"};
      }
      @media ${device.mobileLL} {
        margin: ${(props) => (props.isList ? "12px" : "0")} 0
          ${(props) => (props.isList ? "0 auto" : "0")};
        position: relative;
        right: unset;
        top: unset;
        transform: unset;
      }
    }
    &__group {
      width: 100%;
      max-width: ${(props) => (props.isList ? "318px" : "unset")};
      margin: 0.556vw 0 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      > div {
        width: 1.806vw;
        padding-top: 1.806vw;
      }
      @media ${device.desktopL} {
        margin: 8px 0 0;
        > div {
          width: 26px;
          padding-top: 26px;
        }
      }
      @media ${device.laptopL} {
        > div {
          width: 33px;
          padding-top: 33px;
        }
      }
      @media ${device.tabletL} {
        > div {
          width: 28px;
          padding-top: 28px;
        }
      }
      @media ${device.tablet} {
        > div {
          width: 33px;
          padding-top: 33px;
        }
      }
    }
  }
`;
const Headline = styled.div`
  width: 100%;
  margin: 1.25vw 0 0;
  display: flex;
  align-items: center;
  @media ${device.desktopL} {
    margin: 18px 0 0;
  }
  @media ${device.laptop} {
    flex-wrap: wrap;
    margin: 0;
    justify-content: space-between;
  }
  @media ${device.mobileLL} {
    justify-content: unset;
  }
  .headline {
    &__left {
      width: calc(100% - 12.5vw - 1.111vw - 5.833vw);
      display: flex;
      align-items: center;
      @media ${device.desktopL} {
        width: calc(100% - 180px - 16px - 84px);
      }
      @media ${device.laptop} {
        width: auto;
        flex-wrap: wrap;
      }
      @media ${device.mobileLL} {
        width: 100%;
        margin: 0 0 10px;
      }
    }
    &__head {
      @media (max-width: 395px) {
        margin: 0 22px 0 0;
      }
    }
  }
`;
const Filter = styled.div`
  position: relative;
  z-index: 999;
  width: 100%;
  background: #ffffff;
  margin: 1.25vw 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media ${device.desktopL} {
    margin: 18px 0 0;
  }
  @media ${device.laptop} {
    flex-wrap: wrap;
  }
  .filter {
    &__box {
      &--1 {
        // width: 28%;
        width: 45%;
      }
      &--2 {
        // width: 21%;
        width: 25%;
      }
      @media ${device.laptop} {
        width: calc(50% - 8px);
        margin: 0 0 16px;
      }
      @media ${device.mobileLL} {
        width: 100%;
      }
    }
  }
`;
const Container = styled.div<IList>`
  width: cal(100% + 4.444vw);
  margin: -2.222vw -2.222vw 0;
  @media ${device.desktopL} {
    width: cal(100% + 64px);
    margin: -32px -32px 0;
  }
  .detail {
    &__btn {
      width: 12.5vw;
      margin: 0 auto;
      font-family: "SukhumvitSet-Bold", sans-serif;
      font-weight: 500;
      @media ${device.desktopL} {
        width: 180px;
      }
    }
    &__head {
      border-radius: 2.778vw 0px 0px 0;
      padding: 1.111vw 2.222vw 1.389vw;
      background: #ffffff;
      @media ${device.desktopL} {
        border-radius: 40px 0px 0px 0;
        padding: 16px 32px 20px;
      }
      @media ${device.laptop} {
        border-radius: 0px;
        padding: 20px 32px;
        // top: -140px !important;
      }
      @media ${device.mobileLL} {
        padding: 18px;
        // top: -274px !important;
        // top: -356px !important;
      }
    }
    &__top {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      @media ${device.mobileLL} {
        flex-wrap: wrap;
      }
    }
    &__text {
      display: flex;
      align-items: center;
      line-height: 3.75vw;
      > img {
        width: 1.528vw;
        margin: 0 0.208vw 0 0;
      }
      > span {
        margin: 0 0 0 0.139vw;
      }
      &--hide {
        opacity: 0;
      }
      &--show {
        opacity: 1;
      }
      &--tablet {
        display: none;
      }
      @media ${device.desktopL} {
        line-height: 54px;
        > span {
          margin: 0 0 0 2px;
        }
        > img {
          width: 22px;
          margin: 0 3px 0 0;
        }
      }
      @media ${device.laptop} {
        &--tablet {
          display: flex;
        }
        &--show {
          display: none;
        }
        &--hide {
          display: none;
        }
      }
    }
    &__warp {
      min-height: 70vh;
      &--100 {
        display: flex;
        align-items: center;
      }
    }
    &__content {
      width: ${(props) => (props.isList ? "100%" : "calc(100% + 16px)")};
      padding: 0.556vw 2.778vw 1.667vw;
      margin: 0 ${(props) => (props.isList ? "0" : "-0.556vw")};
      display: ${(props) => (props.isList ? "block" : "flex")};
      flex-wrap: ${(props) => (props.isList ? "unset" : "wrap")};
      @media ${device.desktopL} {
        padding: 8px 40px 24px;
        margin: 0 ${(props) => (props.isList ? "0" : "-8px")};
      }
      @media ${device.mobileLL} {
        width: 100%;
        padding: 8px 18px 24px;
        margin: 0;
      }
    }
  }
`;
